import { useEffect } from "react";
import {
  Building2, Crown, Users, Globe, Sparkles, Calendar, CheckCircle2, XCircle,
  Award, Camera, Radio, BookOpen, Mail, Instagram, Youtube, ArrowRight, Star,
  TrendingUp, Shield, Clock, MapPin, MessageSquare,
} from "lucide-react";

const CONTACT_WHATSAPP = "https://wa.me/13053180109?text=Quiero%20promover%20mi%20hotel";

const Stat = ({ value, label }) => (
  <div className="text-center">
    <p className="text-3xl sm:text-5xl font-black text-amber-400 leading-none">{value}</p>
    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider mt-2">{label}</p>
  </div>
);

const SectionTitle = ({ kicker, title, subtitle }) => (
  <div className="text-center mb-10">
    <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">{kicker}</p>
    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight">{title}</h2>
    {subtitle && <p className="text-slate-300 text-sm sm:text-base mt-3 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>}
  </div>
);

const HotelPromoPage = () => {
  useEffect(() => {
    document.title = "Propuesta Hotel Premium · ClubViajemos";
  }, []);

  const beneficios = [
    { icon: Camera, title: "Producción audiovisual profesional", desc: "Visita mínima de 4 noches documentando cada experiencia de lujo del hotel con equipos propios — sin staff adicional." },
    { icon: Users, title: "Distribución a 5,400+ agentes activos", desc: "Su hotel entra al Catálogo Premium de Destinos Avalados por Carlos. Cada agente lo vende desde el día 1." },
    { icon: Globe, title: "+1 millón de alcance directo", desc: "Publicación en los canales oficiales con más de 1M de seguidores + el efecto multiplicador de la red." },
    { icon: Radio, title: "Webinar EN VIVO desde su hotel", desc: "Si las fechas coinciden, transmisión en vivo a miles de agentes en tiempo real desde sus instalaciones." },
    { icon: Award, title: "Catálogo Premium permanente", desc: "Un sello de confianza que 5,400 profesionales del viaje respaldan y venden activamente — durante años." },
    { icon: Sparkles, title: "Sin costo publicitario", desc: "Cero pagos en efectivo. Cero tarifa publicitaria. Solo hospitalidad genuina: 4 noches con comidas y traslados." },
  ];

  const comparativa = [
    { criterio: "Costo de producción", trad: "$15,000+", influ: "$8,000–$25,000", cv: "$0" },
    { criterio: "Alcance garantizado", trad: "Local/regional", influ: "Variable", cv: "+1M contactos" },
    { criterio: "Audiencia calificada", trad: "Genérica", influ: "Lifestyle", cv: "Agentes de viaje" },
    { criterio: "Red de ventas activa", trad: "No", influ: "No", cv: "5,400+ agentes" },
    { criterio: "Credibilidad industria", trad: "Baja", influ: "Media", cv: "Muy alta" },
    { criterio: "Contenido reutilizable", trad: "Limitado", influ: "1 post/reel", cv: "Multi-formato" },
    { criterio: "Venta directa hotelera", trad: "Indirecta", influ: "Indirecta", cv: "Directa" },
    { criterio: "Catálogo Premium", trad: "No aplica", influ: "No aplica", cv: "Incluido" },
    { criterio: "Webinars en vivo", trad: "No", influ: "No", cv: "Sí — desde su hotel" },
    { criterio: "Inversión requerida", trad: "$15K–$40K", influ: "$8K–$25K", cv: "4 noches + comidas + traslados" },
  ];

  const inversion = [
    { item: "Producción audiovisual profesional (4 días)", mercado: "$15,000 – $25,000" },
    { item: "Campaña de influencer marketing LATAM", mercado: "$8,000 – $25,000" },
    { item: "Distribución a red de agencias activas", mercado: "$3,000 – $8,000/mes" },
    { item: "Contenido multi-formato (reel, video, stories)", mercado: "$4,000 – $6,000" },
    { item: "Webinar en vivo desde su hotel (si coincide fecha)", mercado: "$2,000 – $5,000" },
    { item: "Traslados aeropuerto + alimentación 4 noches", mercado: "$1,500 – $3,000" },
    { item: "5,400+ agentes vendiendo activamente", mercado: "$500+/mes en comisiones" },
  ];

  const pasos = [
    { n: "01", title: "Acuerdo y fecha convenida", desc: "Coordinamos la visita en la fecha de menor impacto en ocupación. Mínimo 4 noches en habitación premium o suite para dos personas." },
    { n: "02", title: "Producción durante la estancia", desc: "Carlos graba la experiencia completa: llegada, check-in, habitación, gastronomía, spa, amenidades y entorno. Maneja personalmente todos los equipos de audio y video." },
    { n: "03", title: "Edición y postproducción", desc: "El equipo editorial de ClubViajemos procesa el material y entrega contenido multi-formato: video largo, reels, stories y material de ventas." },
    { n: "04", title: "Catálogo Premium y distribución", desc: "El hotel entra al Catálogo Premium de Destinos Avalados por Carlos. El contenido se distribuye a 5,400+ agentes con herramientas de venta. Usted recibe reservas — no facturas." },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a14]" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="hotelpromo-page">
      {/* TOP STRIP */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-[#1A1A2E] text-center py-2 text-xs sm:text-sm font-black tracking-wider sticky top-0 z-40">
        <Crown className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        PROPUESTA EXCLUSIVA · HOTELES DE LUJO · 2026 / 2027
      </div>

      {/* HERO */}
      <section className="relative px-4 pt-16 pb-12 sm:pt-20 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D1B2A] via-[#13132a] to-[#0a0a14] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-400/15 rounded-full blur-[160px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="inline-block bg-amber-400/15 text-amber-400 text-[11px] sm:text-xs uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-6 border border-amber-400/30">
            Carlos Miami Fernández · CEO ClubViajemos
          </p>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white leading-[0.95] tracking-tight mb-5">
            Llenamos su hotel<br />
            <span className="text-amber-400">de reservas reales</span>
          </h1>

          <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-3">
            Una propuesta para directores y dueños de hoteles premium: producción audiovisual profesional, distribución a 5,400+ agentes activos y exposición a +1 millón de personas.
          </p>
          <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto mb-8">
            <strong className="text-amber-300">Su única inversión:</strong> 4 noches en habitación premium para 2 personas, con comidas y traslados.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 sm:gap-8 max-w-3xl mx-auto py-6 border-y border-white/10">
            <Stat value="36" label="Años de experiencia" />
            <Stat value="17" label="En turismo" />
            <Stat value="5,400+" label="Agentes en red" />
            <Stat value="+1M" label="Alcance total" />
          </div>

          {/* CTA Hero */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={CONTACT_WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hotelpromo-whatsapp-hero"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#1A1A2E] font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
            >
              <MessageSquare className="h-5 w-5" /> Hablar con Carlos por WhatsApp
            </a>
            <a
              href="#propuesta"
              data-testid="hotelpromo-scroll-cta"
              className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all"
            >
              Ver propuesta completa <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CARTA PERSONAL */}
      <section id="propuesta" className="px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#13132a] via-[#1a1a2e] to-[#13132a] border border-amber-400/20 rounded-2xl p-8 sm:p-12">
          <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-3">Carta personal</p>
          <h2 className="text-2xl sm:text-3xl font-black text-white mb-6 leading-tight">Al Director / Gerente General del hotel</h2>
          <div className="space-y-4 text-slate-300 text-sm sm:text-base leading-relaxed">
            <p>
              Mi nombre es <strong className="text-amber-300">Carlos Miami Fernández</strong>, CEO y fundador de ClubViajemos, la red de agencias de viaje independientes más grande del mundo de habla hispana.
            </p>
            <p>
              Mi sueño es viajar como embajador de una comunidad de más de <strong>5,400 agentes de viajes</strong> que se ganan la vida vendiendo destinos. Y cada destino que conozco, lo convierto en una historia que mi red activa empuja durante meses.
            </p>
            <p>
              No quiero visitar su hotel como un huésped más. Quiero llegar con mi equipo de producción, mi audiencia y mi red lista para vender. <strong className="text-amber-300">Cada rincón del hotel se convierte en escenario de una historia</strong> que motiva agentes y atrae clientes.
            </p>
            <p className="border-l-4 border-amber-400 pl-4 italic text-amber-200">
              "Hagámosla conocida juntos. Usted tiene la propiedad. Yo tengo la red, la credibilidad y la pasión."
            </p>
          </div>
        </div>
      </section>

      {/* BENEFICIOS DEL HOTEL */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            kicker="Qué recibe su hotel"
            title="6 beneficios concretos. Una sola visita."
            subtitle="Todo lo que costaría replicar por vías convencionales — ya está incluido sin pago publicitario."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {beneficios.map((b, i) => (
              <div key={i} className="bg-gradient-to-br from-[#13132a] to-[#0f0f1f] border border-white/10 hover:border-amber-400/40 rounded-2xl p-6 transition-all hover:-translate-y-1">
                <div className="w-12 h-12 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center mb-4">
                  <b.icon className="h-6 w-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-black text-white mb-2 leading-tight">{b.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOINT VENTURE - GANAR GANAR GANAR */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.03] to-transparent">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            kicker="Joint Venture"
            title="Ganar · Ganar · Ganar"
            subtitle="No es una relación proveedor-cliente. Es crecimiento mutuo donde cada parte impulsa a la otra."
          />
          <div className="grid sm:grid-cols-3 gap-5">
            <div className="bg-[#13132a] border border-amber-400/30 rounded-2xl p-6">
              <Building2 className="h-8 w-8 text-amber-400 mb-3" />
              <h3 className="text-xl font-black text-white mb-3">HOTEL</h3>
              <ul className="text-sm text-slate-300 space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Entra al Catálogo Premium</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> 5,400+ agentes lo venden desde el día 1</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Reservas, no facturas</li>
              </ul>
            </div>
            <div className="bg-[#13132a] border border-amber-400/30 rounded-2xl p-6">
              <Crown className="h-8 w-8 text-amber-400 mb-3" />
              <h3 className="text-xl font-black text-white mb-3">CARLOS</h3>
              <ul className="text-sm text-slate-300 space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Viaja y crece su red</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Motiva a sus agentes</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Vive el viaje que cualquier agente quisiera</li>
              </ul>
            </div>
            <div className="bg-[#13132a] border border-amber-400/30 rounded-2xl p-6">
              <Users className="h-8 w-8 text-amber-400 mb-3" />
              <h3 className="text-xl font-black text-white mb-3">AGENTES</h3>
              <ul className="text-sm text-slate-300 space-y-2">
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Se motivan al ver el sueño en acción</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Inspiran ventas con pasión</li>
                <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" /> Suman nuevos agentes cada día</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARATIVA */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <SectionTitle
            kicker="Análisis comparativo"
            title="ClubViajemos vs. canales tradicionales"
            subtitle="Evalúe esta propuesta frente a las alternativas convencionales de marketing hotelero."
          />
          <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#13132a]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-wider text-slate-400">Criterio</th>
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-wider text-slate-400">Marketing tradicional</th>
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-wider text-slate-400">Influencer lifestyle</th>
                  <th className="text-left p-4 font-mono text-[10px] uppercase tracking-wider text-amber-400">ClubViajemos + Carlos</th>
                </tr>
              </thead>
              <tbody>
                {comparativa.map((row, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-white font-semibold">{row.criterio}</td>
                    <td className="p-4 text-slate-400">{row.trad}</td>
                    <td className="p-4 text-slate-400">{row.influ}</td>
                    <td className="p-4 text-amber-300 font-bold">{row.cv}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* INVERSIÓN */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.03] to-transparent">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            kicker="Cuadro de inversión"
            title="Lo que costaría replicar esto en el mercado"
            subtitle="Cada componente de la propuesta, valorado por separado. Total estimado: $29,000 – $64,000."
          />
          <div className="bg-[#13132a] border border-white/10 rounded-2xl overflow-hidden">
            {inversion.map((row, i) => (
              <div key={i} className="flex items-center justify-between p-4 border-b border-white/5 hover:bg-white/[0.02]">
                <span className="text-white text-sm flex-1 pr-3">{row.item}</span>
                <div className="text-right flex items-center gap-4">
                  <span className="text-rose-400 line-through text-sm font-mono">{row.mercado}</span>
                  <span className="text-amber-400 text-sm font-black uppercase">$0</span>
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-400/10 via-amber-400/15 to-amber-400/10 border-t-2 border-amber-400/40">
              <span className="text-white font-black uppercase tracking-wider">TOTAL ESTIMADO</span>
              <div className="text-right">
                <p className="text-rose-400 line-through text-base font-mono">$29,000 – $64,000</p>
                <p className="text-amber-400 text-base sm:text-lg font-black">4 noches · comidas · traslados</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA - PASOS */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            kicker="El proyecto"
            title="Cómo funciona la visita — paso a paso"
            subtitle="Un proceso transparente. Sin contratos complejos ni exclusividades."
          />
          <div className="space-y-4">
            {pasos.map((p) => (
              <div key={p.n} className="bg-[#13132a] border border-white/10 hover:border-amber-400/30 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row gap-5 items-start transition-all">
                <div className="text-5xl sm:text-6xl font-black text-amber-400/60 leading-none flex-shrink-0 font-mono">
                  {p.n}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-xl font-black text-white mb-2">{p.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LO QUE PEDIMOS / LO QUE NO PEDIMOS */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.03] to-transparent">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            kicker="Nuestra solicitud"
            title="Simple. Transparente. Sin letra chica."
          />
          <div className="grid sm:grid-cols-2 gap-5">
            {/* PEDIMOS */}
            <div className="bg-[#13132a] border-2 border-emerald-400/30 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                <h3 className="text-xl font-black text-white uppercase">Lo que pedimos</h3>
              </div>
              <ul className="space-y-3 text-sm text-slate-300">
                {["Mínimo 4 noches en habitación premium para 2 personas","Todas las comidas y bebidas incluidas","Traslado ida y vuelta desde el aeropuerto","Fecha elegida por el hotel — nos adaptamos","Apertura para una promoción conjunta opcional"].map((it,i) => (
                  <li key={i} className="flex gap-3"><CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-1" /><span>{it}</span></li>
                ))}
              </ul>
            </div>
            {/* NO PEDIMOS */}
            <div className="bg-[#13132a] border-2 border-rose-400/20 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-2 mb-5">
                <XCircle className="h-6 w-6 text-rose-400" />
                <h3 className="text-xl font-black text-white uppercase">Lo que NO pedimos</h3>
              </div>
              <ul className="space-y-3 text-sm text-slate-400">
                {["Ningún pago en efectivo ni tarifa publicitaria","Staff adicional ni coordinación de producción","Fechas de alta ocupación — nos adaptamos","Contratos complejos ni exclusividades","Compromiso de ventas — solo hospitalidad auténtica"].map((it,i) => (
                  <li key={i} className="flex gap-3"><XCircle className="h-4 w-4 text-rose-400 flex-shrink-0 mt-1" /><span>{it}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CANALES OFICIALES */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          <SectionTitle
            kicker="Presencia digital verificada"
            title="Canales oficiales + efecto multiplicador"
            subtitle="Más cuentas personales de 5,400+ agentes activos en Latinoamérica."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <a href="https://www.instagram.com/carlosmiami.clubviajemos/" target="_blank" rel="noopener noreferrer" className="bg-[#13132a] border border-white/10 hover:border-pink-400/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5">
              <Instagram className="h-7 w-7 text-pink-400 mb-3" />
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">Instagram</p>
              <p className="text-base text-white font-bold truncate">@carlosmiami.clubviajemos</p>
              <p className="text-xs text-pink-300 mt-1">600K+ seguidores</p>
            </a>
            <a href="https://www.youtube.com/@ClubViajemos" target="_blank" rel="noopener noreferrer" className="bg-[#13132a] border border-white/10 hover:border-red-400/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5">
              <Youtube className="h-7 w-7 text-red-400 mb-3" />
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">YouTube</p>
              <p className="text-base text-white font-bold">@ClubViajemos</p>
              <p className="text-xs text-red-300 mt-1">40K+ suscriptores</p>
            </a>
            <a href="https://www.facebook.com/Clubviajemos" target="_blank" rel="noopener noreferrer" className="bg-[#13132a] border border-white/10 hover:border-blue-400/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5">
              <Globe className="h-7 w-7 text-blue-400 mb-3" />
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">Facebook</p>
              <p className="text-base text-white font-bold">Clubviajemos</p>
              <p className="text-xs text-blue-300 mt-1">Comunidad activa</p>
            </a>
            <a href="https://www.tiktok.com/@clubviajemos" target="_blank" rel="noopener noreferrer" className="bg-[#13132a] border border-white/10 hover:border-cyan-400/40 rounded-2xl p-5 transition-all hover:-translate-y-0.5">
              <Sparkles className="h-7 w-7 text-cyan-400 mb-3" />
              <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">TikTok</p>
              <p className="text-base text-white font-bold">@clubviajemos</p>
              <p className="text-xs text-cyan-300 mt-1">Contenido viral</p>
            </a>
          </div>
          <div className="bg-gradient-to-r from-amber-400/10 via-amber-400/15 to-amber-400/10 border border-amber-400/30 rounded-2xl p-6 text-center">
            <TrendingUp className="h-7 w-7 text-amber-400 mx-auto mb-2" />
            <p className="text-white text-base sm:text-lg font-black">
              + Sub-cuentas: <span className="text-amber-300">5,400+ agentes activos</span> que multiplican el alcance ×10
            </p>
            <p className="text-amber-400 text-xl sm:text-2xl font-black mt-2">+1 MILLÓN DE PERSONAS</p>
            <p className="text-slate-300 text-xs sm:text-sm mt-1">alcanzables al activar la red completa</p>
          </div>
        </div>
      </section>

      {/* CIERRE / CTA FINAL */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto bg-gradient-to-br from-[#1A1A2E] via-[#16213E] to-[#0F3460] border-2 border-amber-400/50 rounded-2xl p-8 sm:p-12 relative overflow-hidden shadow-[0_30px_100px_rgba(212,175,55,0.2)]">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.15),transparent_60%)] pointer-events-none" />
          <div className="relative text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-amber-400 bg-amber-400/10 mb-5">
              <Crown className="h-10 w-10 text-amber-300" />
            </div>
            <p className="text-[11px] text-amber-300 font-mono tracking-[3px] uppercase mb-2">La pregunta final</p>
            <h3 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight">
              No es <em className="text-slate-400 not-italic line-through">si quiere esta alianza</em>.<br />
              La pregunta es: <span className="text-amber-300">¿cuándo quiere empezar?</span>
            </h3>
            <p className="text-slate-300 text-base leading-relaxed mb-7 max-w-xl mx-auto">
              Cada semana que pasa sin esta alianza, 5,400 agentes vendieron otro hotel que sí dijo que sí.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={CONTACT_WHATSAPP}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hotelpromo-whatsapp-final"
                className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-[#1A1A2E] font-black px-8 py-4 rounded-xl uppercase tracking-wider text-base hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
              >
                <MessageSquare className="h-5 w-5" /> Hablar con Carlos
              </a>
              <a
                href="mailto:carlos@clubviajemos.com?subject=Quiero%20promover%20mi%20hotel"
                data-testid="hotelpromo-email-final"
                className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-xl uppercase tracking-wider text-base transition-all"
              >
                <Mail className="h-5 w-5" /> Enviar email
              </a>
            </div>
            <p className="text-xs text-slate-400 mt-6 flex items-center gap-1.5 justify-center flex-wrap">
              <MapPin className="h-3 w-3" /> Miami, Florida · Carlos Miami Fernández · CEO ClubViajemos
            </p>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="py-10 px-4 text-center text-xs text-slate-500 border-t border-white/5">
        <p>&copy; 2026 Club Viajemos / Travel Empire · Carlos Miami Fernández</p>
        <p className="mt-1">36 años de experiencia · 17 en la industria del viaje · Miami, FL</p>
        <p className="mt-3 text-[10px] italic">
          Esta propuesta es confidencial y está destinada exclusivamente al hotel al que fue enviada.
        </p>
        <p className="mt-3">
          <a href="/" className="text-amber-400 hover:underline">← Volver al sitio principal</a>
        </p>
      </footer>
    </div>
  );
};

export default HotelPromoPage;
